/*
  PL - High-variant product loader (products with more than 250 variants)

  Why this exists:
  Shopify only exposes the first 250 variants to the theme, both in Liquid
  (product.variants) and in the /products/<handle>.js endpoint the theme's
  ProductLoader reads. Bundle kits like the Hair Tinsel kits have 676 to 1,936
  variants, so every combination past #250 looked "missing" to the variant
  picker: the swatch was crossed out and Add to Cart could not resolve a variant.

  What it does:
  Wraps window.fetch. When a /products/<handle>.js response comes back with
  exactly 250 variants (Shopify's cap), it pages through the full variant list
  with the tokenless Storefront API and appends the missing variants in the
  same shape the Ajax API uses. Products under 250 variants are untouched and
  cost nothing extra.

  Because it patches the data source, everything that reads the product JSON
  (theme.js ProductVariants, pl-grouped-variants-fix, quick-buy-image-preload)
  sees the full list without changes to those files.

  It also fills the server-rendered availability-message map for the extra
  variants, so the "In Stock" line keeps working past #250.

  PL 2026-10-08 a11y: it also gives each quick-buy drawer/popover load its own
  element ids (see uniquifyQuickBuy). Loaded quick-buy content stays in the DOM
  after closing, and variant-grid collections load the same product into several
  drawers, so without this the ids (form, option inputs, labels, AddToCart)
  repeat and theme.js getElementById(form-id) binds a later drawer to the first.

  Must load BEFORE theme.js (deferred scripts run in document order).
*/
(function () {
  'use strict';

  if (window.__plHighVariantLoader) return;
  window.__plHighVariantLoader = true;
  if (typeof window.fetch !== 'function') return;

  var LIQUID_CAP = 250;
  var API_URL = '/api/2026-01/graphql.json';
  var PAGE_SIZES = [250, 100, 50];
  var PRODUCT_JS_RE = /\/products\/([^\/?#]+)\.js(?:[?#].*)?$/;

  var nativeFetch = window.fetch.bind(window);
  var fullCache = {};
  var extraIdsByHandle = {};
  // Resolved product JSON per handle, kept so the availability maps can be
  // filled synchronously (see the variant:changed listener at the bottom).
  var resolvedByHandle = {};

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  }

  function methodOf(input, init) {
    var m = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET';
    return String(m).toUpperCase();
  }

  function gidToNumber(gid) {
    var n = Number(String(gid).split('/').pop());
    return isNaN(n) ? gid : n;
  }

  function toCents(money) {
    if (!money || money.amount == null) return null;
    return Math.round(parseFloat(money.amount) * 100);
  }

  function buildQuery(pageSize, withSelectedOptions) {
    return 'query PlVariants($handle: String!, $after: String) {' +
      ' product(handle: $handle) {' +
      '  variants(first: ' + pageSize + ', after: $after) {' +
      '   pageInfo { hasNextPage endCursor }' +
      '   nodes { id title availableForSale sku barcode requiresShipping' +
      '    price { amount } compareAtPrice { amount }' +
      (withSelectedOptions ? '    selectedOptions { value }' : '') +
      '   }' +
      '  }' +
      ' }' +
      '}';
  }

  function gql(query, variables) {
    return nativeFetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: query, variables: variables })
    }).then(function (res) {
      if (!res.ok) throw new Error('Storefront API HTTP ' + res.status);
      return res.json();
    }).then(function (data) {
      if (data.errors && data.errors.length) throw new Error(data.errors[0].message || 'Storefront API error');
      return data.data;
    });
  }

  // Page through every variant. Tries the largest page size first and steps
  // down if the tokenless complexity limit rejects it.
  function fetchAllVariants(handle) {
    function attempt(sizeIndex) {
      var size = PAGE_SIZES[sizeIndex];
      // Large pages skip selectedOptions to stay under the complexity limit;
      // option values are then read from the variant title.
      var withOptions = size < 250;
      var query = buildQuery(size, withOptions);
      var all = [];

      function page(after) {
        return gql(query, { handle: handle, after: after || null }).then(function (data) {
          var conn = data && data.product && data.product.variants;
          if (!conn) throw new Error('Product not found');
          all = all.concat(conn.nodes || []);
          if (conn.pageInfo && conn.pageInfo.hasNextPage) return page(conn.pageInfo.endCursor);
          return { nodes: all, withOptions: withOptions };
        });
      }

      return page(null).catch(function (err) {
        if (sizeIndex + 1 < PAGE_SIZES.length) return attempt(sizeIndex + 1);
        throw err;
      });
    }
    return attempt(0);
  }

  function optionValuesFor(node, optionCount, withOptions) {
    if (withOptions && node.selectedOptions) {
      return node.selectedOptions.map(function (o) { return o.value; });
    }
    var parts = String(node.title).split(' / ');
    return parts.length === optionCount ? parts : null;
  }

  function toAjaxVariant(node, product, template, values, mediaByOption1) {
    var v = {
      id: gidToNumber(node.id),
      title: node.title,
      option1: values[0] != null ? values[0] : null,
      option2: values[1] != null ? values[1] : null,
      option3: values[2] != null ? values[2] : null,
      sku: node.sku || null,
      requires_shipping: node.requiresShipping !== false,
      taxable: template ? template.taxable : true,
      featured_image: null,
      available: !!node.availableForSale,
      name: product.title + ' - ' + node.title,
      public_title: node.title,
      options: values.slice(),
      price: toCents(node.price),
      weight: template ? template.weight : 0,
      compare_at_price: toCents(node.compareAtPrice),
      inventory_management: template ? template.inventory_management : 'shopify',
      inventory_policy: template ? template.inventory_policy : 'deny',
      barcode: node.barcode || null,
      quantity_rule: { min: 1, max: null, increment: 1 },
      quantity_price_breaks: [],
      requires_selling_plan: false,
      selling_plan_allocations: []
    };
    // Reuse the image of a known variant with the same first option, so the
    // gallery still switches to the chosen color.
    var media = mediaByOption1[v.option1];
    if (media) {
      v.featured_image = media.featured_image;
      if (media.featured_media) v.featured_media = media.featured_media;
    }
    return v;
  }

  function augment(handle, json) {
    var base = json.variants || [];
    var optionCount = (json.options && json.options.length) || 1;
    var template = base[0] || null;
    var known = {};
    var mediaByOption1 = {};

    base.forEach(function (v) {
      known[String(v.id)] = true;
      if (v.featured_image && !mediaByOption1[v.option1]) {
        mediaByOption1[v.option1] = { featured_image: v.featured_image, featured_media: v.featured_media };
      }
    });

    return fetchAllVariants(handle).then(function (result) {
      var extras = [];
      result.nodes.forEach(function (node) {
        var id = gidToNumber(node.id);
        if (known[String(id)]) return;
        var values = optionValuesFor(node, optionCount, result.withOptions);
        if (!values) return;
        extras.push(toAjaxVariant(node, json, template, values, mediaByOption1));
        known[String(id)] = true;
      });

      if (extras.length) {
        json.variants = base.concat(extras);
        json.available = json.available || extras.some(function (v) { return v.available; });
        extraIdsByHandle[handle] = extras;
        resolvedByHandle[handle] = json;
        fillAvailabilityMaps(handle);
      }
      return json;
    });
  }

  function getFullProduct(handle, response) {
    if (fullCache[handle]) return fullCache[handle];
    var promise = response.clone().json().then(function (json) {
      if (!json || !json.variants || json.variants.length < LIQUID_CAP) return json;
      return augment(handle, json).catch(function (err) {
        // Never break the page: fall back to the original 250 variants.
        if (window.console && console.warn) console.warn('[pl-high-variant-loader]', handle, err);
        return json;
      });
    });
    fullCache[handle] = promise;
    // If the base response was not augmentable, let later calls retry.
    promise.catch(function () { delete fullCache[handle]; });
    return promise;
  }

  /* ---- Unique ids per quick-buy load (PL 2026-10-08 a11y, WCAG 1.3.1/4.1.2) ---- */

  // theme.js OpenableElement._load fetches ?view=quick-buy-drawer|popover and
  // assigns the HTML with innerHTML, which connects the custom elements at once.
  // Rewriting the response text first means they connect with unique ids. Every
  // id inside the quick-buy element gets the same "-qb<n>" suffix and references
  // to those ids are updated, so pairings such as volume-drawer-<x> /
  // product-form-<x> still match. References to ids outside the fragment are
  // left alone. Any failure returns the original HTML.
  var QUICK_BUY_RE = /[?&]view=quick-buy-(?:drawer|popover)(?:[&#]|$)/;
  var ID_REF_ATTRS = ['for', 'form', 'form-id', 'aria-controls', 'aria-labelledby', 'aria-describedby',
    'aria-owns', 'aria-activedescendant', 'aria-errormessage', 'aria-details', 'data-bind-value', 'observe-form'];
  var ID_REF_SELECTOR = ID_REF_ATTRS.map(function (a) { return '[' + a + ']'; }).join(',');
  var quickBuySeq = 0;

  function uniquifyIds(html) {
    if (typeof DOMParser !== 'function') return html;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var root = doc.querySelector('quick-buy-drawer, quick-buy-popover');
    if (!root) return html;
    var suffix = '-qb' + (++quickBuySeq);
    var renamed = {};
    var seen = {};
    Array.prototype.forEach.call(root.querySelectorAll('[id]'), function (el) {
      var id = el.getAttribute('id');
      if (!id) return;
      var next = id + suffix;
      if (seen[id]) next += '-' + (++seen[id]); // repeated id: references keep the first, as getElementById does
      else { seen[id] = 1; renamed[id] = next; }
      el.setAttribute('id', next);
    });
    Array.prototype.forEach.call(root.querySelectorAll(ID_REF_SELECTOR), function (el) {
      ID_REF_ATTRS.forEach(function (attr) {
        var value = el.getAttribute(attr);
        if (!value) return;
        var updated = value.split(/\s+/).map(function (t) { return renamed[t] || t; }).join(' ');
        if (updated !== value.trim()) el.setAttribute(attr, updated);
      });
    });
    return '<!doctype html>' + doc.documentElement.outerHTML;
  }

  function uniquifyQuickBuy(response) {
    if (!response.ok) return response;
    return response.text().then(function (text) {
      var out = text;
      try { out = uniquifyIds(text); } catch (e) { out = text; }
      return new Response(out, {
        status: response.status,
        statusText: response.statusText,
        headers: { 'Content-Type': response.headers.get('content-type') || 'text/html; charset=utf-8' }
      });
    });
  }

  // The host <quick-buy-drawer|popover> sits outside the fetched fragment, so an
  // aria-labelledby/-describedby on it still names the unsuffixed id. Point it
  // at the renamed element once the content is in (load:end does not bubble).
  document.addEventListener('openable-element:load:end', function (e) {
    var host = e.target;
    if (!host || !host.matches || !host.matches('quick-buy-drawer, quick-buy-popover')) return;
    ['aria-labelledby', 'aria-describedby'].forEach(function (attr) {
      var value = host.getAttribute(attr);
      if (!value) return;
      var updated = value.split(/\s+/).map(function (t) {
        if (!t || host.querySelector('[id="' + t + '"]')) return t;
        var el = host.querySelector('[id^="' + t + '-qb"]');
        return el ? el.getAttribute('id') : t;
      }).join(' ');
      if (updated !== value.trim()) host.setAttribute(attr, updated);
    });
  }, true);

  window.fetch = function (input, init) {
    var url = urlOf(input);
    var isGet = url && methodOf(input, init) === 'GET';
    if (isGet && QUICK_BUY_RE.test(url)) return nativeFetch(input, init).then(uniquifyQuickBuy);
    var match = isGet ? url.match(PRODUCT_JS_RE) : null;
    if (!match) return nativeFetch(input, init);

    var handle = decodeURIComponent(match[1]);
    return nativeFetch(input, init).then(function (response) {
      if (!response.ok) return response;
      var type = response.headers.get('content-type') || '';
      if (type.indexOf('json') === -1 && type.indexOf('javascript') === -1) return response;

      return getFullProduct(handle, response).then(function (json) {
        return new Response(JSON.stringify(json), {
          status: response.status,
          statusText: response.statusText,
          headers: { 'Content-Type': 'application/json; charset=utf-8' }
        });
      }).catch(function () { return response; });
    });
  };

  /* ---- Availability message map ("In Stock" line) for the extra variants ---- */

  // Most common message among base variants with the same availability. Using
  // the most common one avoids copying a one-off "Only 2 left" message.
  // Values are message indexes (numbers); null when the list is empty.
  function modeOf(list) {
    var counts = {};
    var best = null;
    var bestCount = 0;
    list.forEach(function (html) {
      counts[html] = (counts[html] || 0) + 1;
      if (counts[html] > bestCount) { best = html; bestCount = counts[html]; }
    });
    return best;
  }

  // PL 2026-10-08 perf: product-form.liquid now renders the deduplicated
  // {m: [message html], v: {id: index into m}}; the old flat {id: html} is
  // still handled. Works on message indexes so the map stays deduplicated.
  var SOLD_OUT_HTML = '<span class="inventory inventory--soldout icon-text small">Sold out</span>';

  function fillOneMap(mapEl, extras, baseAvailability) {
    var raw;
    try { raw = JSON.parse(mapEl.textContent); } catch (e) { return; }
    if (!raw || typeof raw !== 'object') return;
    var packed = !!(raw.v && raw.m);
    var msgs = packed ? raw.m : [];
    var map = packed ? raw.v : {};
    if (!packed) {
      // Flat map: index its messages so both formats share the logic below.
      Object.keys(raw).forEach(function (id) {
        var i = msgs.indexOf(raw[id]);
        if (i === -1) { i = msgs.length; msgs.push(raw[id]); }
        map[id] = i;
      });
    }

    var inStock = [];
    var soldOut = [];
    Object.keys(map).forEach(function (id) {
      if (msgs[map[id]] == null) return;
      if (baseAvailability[id] === true) inStock.push(map[id]);
      else if (baseAvailability[id] === false) soldOut.push(map[id]);
    });
    var inStockIdx = modeOf(inStock);
    if (inStockIdx == null) return;
    var soldOutIdx = modeOf(soldOut);
    if (soldOutIdx == null) {
      soldOutIdx = msgs.indexOf(SOLD_OUT_HTML);
      if (soldOutIdx === -1) { soldOutIdx = msgs.length; msgs.push(SOLD_OUT_HTML); }
    }

    var changed = false;
    extras.forEach(function (v) {
      var key = String(v.id);
      if (map[key] != null) return;
      map[key] = v.available ? inStockIdx : soldOutIdx;
      changed = true;
    });
    if (!changed) return;
    if (packed) {
      mapEl.textContent = JSON.stringify(raw);
    } else {
      var flat = {};
      Object.keys(map).forEach(function (id) { flat[id] = msgs[map[id]]; });
      mapEl.textContent = JSON.stringify(flat);
    }
  }

  // Synchronous on purpose: the variant:changed capture listener must finish
  // filling before pl-subtotal.js reads the map in the same event.
  function fillAvailabilityMaps(handle) {
    var extras = extraIdsByHandle[handle];
    var json = resolvedByHandle[handle];
    if (!extras || !extras.length || !json) return;

    var baseAvailability = {};
    (json.variants || []).forEach(function (v) { baseAvailability[String(v.id)] = v.available; });

    // A map that belongs to a different product has no ids in common with
    // this one, so fillOneMap leaves it alone. Scanning the whole document
    // keeps this working for quick-buy drawers too.
    Array.prototype.forEach.call(document.querySelectorAll('[data-availability-map]'), function (mapEl) {
      fillOneMap(mapEl, extras, baseAvailability);
    });
  }

  // Quick-buy drawers render their form after the product data has loaded, so
  // fill again whenever a variant changes (capture phase runs before
  // pl-subtotal.js reads the map).
  document.addEventListener('variant:changed', function () {
    Object.keys(extraIdsByHandle).forEach(fillAvailabilityMaps);
  }, true);
})();
